import { useState } from "react";
import { Check, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useCountries } from "@/hooks/useCountries";
import { countryName } from "@/lib/countries.functions";
import { useLocale } from "@/lib/i18n/locale-context";
import { cn } from "@/lib/utils";

type Props = {
  /** Landet annonsen läggs upp i. */
  value: string;
  onChange: (code: string) => void;
  /** Besökarens eget land – används bara för varningstexten. */
  homeCountry?: string;
  className?: string;
};

/** Kompakt fält med flagga och landets namn, plus varning när man byter land. */
export function AdCountryPicker({ value, onChange, homeCountry, className }: Props) {
  const { lang } = useLocale();
  const countries = useCountries();
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState<string | null>(null);

  const current = countries.find((c) => c.code === value) ?? countries[0];
  const home = countries.find((c) => c.code === (homeCountry ?? value));
  const target = countries.find((c) => c.code === pending);

  const t =
    lang === "es"
      ? {
          label: "País del anuncio",
          confirmTitle: "¿Cambiar de país?",
          confirmBody: (from: string, to: string) =>
            `Pareces estar en ${from}. ¿Seguro que quieres publicar este anuncio en ${to}?`,
          yes: "Sí, publicar en",
          cancel: "Cancelar",
        }
      : lang === "en"
        ? {
            label: "Listing country",
            confirmTitle: "Change country?",
            confirmBody: (from: string, to: string) =>
              `You seem to be in ${from}. Are you sure you want to publish this listing in ${to}?`,
            yes: "Yes, publish in",
            cancel: "Cancel",
          }
        : {
            label: "Annonsens land",
            confirmTitle: "Byta land?",
            confirmBody: (from: string, to: string) =>
              `Du verkar vara i ${from}. Är du säker på att du vill lägga upp annonsen i ${to}?`,
            yes: "Ja, lägg upp i",
            cancel: "Avbryt",
          };

  const pick = (code: string) => {
    setOpen(false);
    if (code === value) return;
    if (home && home.code !== code) {
      setPending(code);
      return;
    }
    onChange(code);
  };

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            aria-label={t.label}
            className={cn(
              "h-10 min-w-0 justify-start gap-2 rounded-md border-white/20 bg-ocean-900/40 px-3 text-sm font-medium text-ocean-100 hover:bg-ocean-900/60 hover:text-white",
              className,
            )}
          >
            <span aria-hidden className="shrink-0 text-base leading-none">
              {current?.flag}
            </span>
            <span className="min-w-0 flex-1 truncate text-left">
              {current ? countryName(current, lang) : value}
            </span>
            <ChevronDown className="h-4 w-4 shrink-0 opacity-70" />
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-56 p-1">
          {countries.map((c) => (
            <Button
              key={c.code}
              type="button"
              variant="ghost"
              onClick={() => pick(c.code)}
              className="h-10 w-full justify-start gap-2 rounded-md px-2 text-left text-sm"
            >
              <span aria-hidden className="text-base leading-none">
                {c.flag}
              </span>
              <span className="flex-1">{countryName(c, lang)}</span>
              <span className="text-xs text-muted-foreground">{c.currency}</span>
              {c.code === value && <Check className="h-4 w-4 text-primary" />}
            </Button>
          ))}
        </PopoverContent>
      </Popover>

      <AlertDialog open={!!pending} onOpenChange={(o) => !o && setPending(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t.confirmTitle}</AlertDialogTitle>
            <AlertDialogDescription>
              {home && target
                ? t.confirmBody(countryName(home, lang), countryName(target, lang))
                : null}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t.cancel}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (pending) onChange(pending);
                setPending(null);
              }}
            >
              {t.yes} {target ? countryName(target, lang) : ""}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
